import React, { createContext, ReactNode, useContext, useMemo, useState } from 'react';

import type { UserInformationAddress } from '@rover/rsdk/src/apiClient/latest/model/userInformationAddress';

import { formatAddress } from '../utils';

type TemporaryAddressContextType = {
  formattedAddress: string;
  address: UserInformationAddress | null;
  setTemporaryAddress: React.Dispatch<React.SetStateAction<UserInformationAddress | null>>;
  enabled: boolean;
};

export const TemporaryAddressContext = createContext<TemporaryAddressContextType | undefined>(
  undefined
);

type TemporaryAddressProviderProps = {
  children: ReactNode;
  initialAddress?: Partial<UserInformationAddress> | null;
};

export const TemporaryAddressProvider: React.FC<TemporaryAddressProviderProps> = ({
  children,
  initialAddress = null,
}) => {
  const [temporaryAddress, setTemporaryAddress] = useState<UserInformationAddress | null>(
    initialAddress as UserInformationAddress | null
  );

  const formattedAddress = useMemo(() => {
    return formatAddress(temporaryAddress);
  }, [temporaryAddress]);

  const value = useMemo(
    () => ({
      formattedAddress,
      address: temporaryAddress,
      setTemporaryAddress,
      enabled: Boolean(temporaryAddress),
    }),
    [formattedAddress, temporaryAddress, setTemporaryAddress]
  );

  return (
    <TemporaryAddressContext.Provider value={value}>{children}</TemporaryAddressContext.Provider>
  );
};

/**
 * Hook to access temporary address state
 * Returns formatted address string, address object, and methods to update/clear
 * Used for session-based addresses that should be shown as options but aren't the user's home address
 *
 * Must be used within a TemporaryAddressProvider
 */
const useTemporaryAddress = (): TemporaryAddressContextType => {
  const context = useContext(TemporaryAddressContext);

  if (context === undefined) {
    // Return a default "disabled" state if used outside provider
    return {
      formattedAddress: '',
      address: null,
      setTemporaryAddress: () => {},
      enabled: false,
    };
  }

  return context;
};

export default useTemporaryAddress;
